'use client';

import SettingConfig from '@module/config/setting';
import useModalStore from '@module/stores/useModalStore';
import type { ASPUtils } from '@module/utils/asp/asp-utils';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

/**
 * ASP 부트스트랩 이후 ASPUtils 가 발행하는 이벤트를 React 컨텍스트로 연결한다.
 *
 *  - `navigate` : 내부 라우팅 요청. `options.replace` 가 true 면 history 를 교체,
 *                 아니면 push (`emitNavigate` 는 /auth 승인 직후 replace 로 emit 한다).
 *  - `no-access`: 인증 실패 시 노출할 모달. ASPConfigManager 가 모달이 마운트될
 *                 때까지 폴링하기 때문에 `checker.arrived` 로 1회만 모달을 띄운다.
 */
function ASPRouteGuard({ children }: { children?: React.ReactNode }) {
  const [valid, setValid] = useState(true);
  const router = useRouter();

  useEffect(() => {
    if (!SettingConfig.aspUtils) return;
    const aspUtils = SettingConfig.aspUtils;

    const onNavigate = (url: string, options?: { replace?: boolean }) => {
      if (options?.replace) router.replace(url);
      else router.push(url);
    };

    const onNoAccess = (message: string, checker: { closed: boolean; arrived: boolean; aspUtils: ASPUtils }) => {
      setValid(false);
      if (checker.arrived) return;
      checker.arrived = true;
      useModalStore.getState().pushModal({
        type: 'CONFIRM_MODAL',
        options: {
          description: message,
          onClose: () => {
            checker.closed = true;
          },
        },
      });
    };

    aspUtils.on('navigate', onNavigate);
    aspUtils.on('no-access', onNoAccess);
    return () => {
      aspUtils.off('navigate', onNavigate);
      aspUtils.off('no-access', onNoAccess);
    };
  }, [router]);

  if (!valid) return null;
  return <>{children}</>;
}

export default ASPRouteGuard;
